import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { Offcanvas, Card, Row, Col } from 'react-bootstrap';

const RevenueRecognitionReport = () => {
  const [monthlyData, setMonthlyData] = useState([]);
  const [selectedMonthData, setSelectedMonthData] = useState(null);
  const [projectDetails, setProjectDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showOffcanvas, setShowOffcanvas] = useState(false);

  // Custom AED style formatting without currency label
  const formatAEDStyle = (amount) => {
    return Number(amount || 0).toLocaleString('en-AE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  useEffect(() => {
    fetchMonthlySummary();
  }, []);

  const fetchMonthlySummary = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');
      const token = userInfo?.token;

      const response = await axios.get(`${backendUrl}/api/revenue-recognition/summary`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Tenant-Origin': origin,
        },
      });

      if (response.data.status === 'success') {
        setMonthlyData(response.data.data);
      } else {
        console.error('Failed to fetch monthly summary:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching monthly summary:', error);
    }
  };

  const handleMonthClick = async (month, year, totalAmount, initial = false) => {
    try {
      setLoading(true);
      setSelectedMonthData({ month, year, totalAmount });

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');
      const token = userInfo?.token;

      const response = await axios.get(`${backendUrl}/api/revenue-recognition/project-details`, {
        params: { month, year, initial: false },

        headers: {
          Authorization: `Bearer ${token}`,
          'Tenant-Origin': origin,
        },
        skipLoader: true,
      });

      if (response.data.status === 'success') {
        setProjectDetails(response.data.data);
        console.log('Fetched project details:', response.data.data);
        setShowOffcanvas(true);
      } else {
        console.error('Failed to fetch project details:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
    } finally {
      setLoading(false);
    }
  };

  const closeOffcanvas = () => {
    setShowOffcanvas(false);
    setSelectedMonthData(null);
    setProjectDetails([]);
  };

  const formatMonthYear = (month, year) => {
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  if (!monthlyData.length && loading) {
    return <div className="text-center p-4">Loading revenue recognition data...</div>;
  }

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          {/* Page Title */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">Revenue Recognition Report</h4>
              </div>
            </div>
          </div>
          <br />

          {/* Monthly Summary Table */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table align-middle table-nowrap table-check table-list">
                      <thead className="table-light">
                        <tr>
                          <th>Month</th>
                          <th>Year</th>
                          <th className="text-end">Recognized Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyData.map((item) => (
                          <tr key={`${item.year}-${item.month}`}>
                            <td><strong>{formatMonthYear(item.month, item.year)}</strong></td>
                            <td>{item.year}</td>
                            <td className="text-end">
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleMonthClick(item.month, item.year, item.total_amount)}
                                disabled={loading}
                              >
                                {formatAEDStyle(item.total_amount)}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* React-Bootstrap Offcanvas */}
          <Offcanvas 
            show={showOffcanvas} 
            onHide={closeOffcanvas} 
            placement="end"
            style={{ width: '600px' }}
          >
            <Offcanvas.Header closeButton className="bg-light">
              <Offcanvas.Title>
                <div>
                  <h5 className="offcanvas-title">
                    Project Details - {selectedMonthData ? formatMonthYear(selectedMonthData.month, selectedMonthData.year) : ''}
                  </h5>
                  <p className="text-muted mb-0">
                    Total Recognized: {selectedMonthData ? formatAEDStyle(selectedMonthData.totalAmount) : '0.00'}
                  </p>
                </div>
              </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              {loading ? (
                <div className="text-center p-4">Loading project details...</div>
              ) : projectDetails.length > 0 ? (
                <div className="project-cards-container">
                  {/* Project Cards - One per row */}
                  <Row className="g-3">
                    {projectDetails.map((project) => (
                      <Col key={project.id} xs={12}>
                        <Card className="h-100 project-card">
                          <Card.Header className="bg-transparent">
                            <Card.Title className="mb-1" style={{ fontSize: '1.1rem' }}>
                              {project.project?.name || 'Unnamed Project'}
                            </Card.Title>
                            <small className="text-muted">Project #: {project.project?.project_number}</small>
                          </Card.Header>
                          
                          <Card.Body className="py-2">
                            {/* Client Information */}
                            <div className="mb-2">
                              <strong className="d-block text-sm">Client</strong>
                              <span className="text-dark">{project.project?.client?.name || 'N/A'}</span>
                            </div>

                            {/* Dates */}
                            <div className="row mb-2">
                              <div className="col-6">
                                <strong className="d-block text-sm">Start Date</strong>
                                <small className="text-dark">
                                  {formatDate(project.project?.subscription_start_date)}
                                </small>
                              </div>
                              <div className="col-6">
                                <strong className="d-block text-sm">End Date</strong>
                                <small className="text-dark">
                                  {formatDate(project.project?.subscription_due_date)}
                                </small>
                              </div>
                            </div>

                            {/* Amounts */}
                            <div className="row">
                              <div className="col-6">
                                <strong className="d-block text-sm">Total Amount</strong>
                               <span className="text-dark fw-bold">
  {formatAEDStyle(
    project.project?.client?.currency === 'AED'
      ? project.project?.total_amount / 1.05  // remove 5% VAT
      : project.project?.total_amount           // leave as is
  )}
</span>

                              </div>
                              <div className="col-6">
                                <strong className="d-block text-sm">Recognized</strong>
                                <span className="badge bg-success fs-6">
                                  {formatAEDStyle(project.recognized_amount)}
                                </span>
                              </div>
                            </div>
                          </Card.Body>

                          <Card.Footer className="bg-transparent py-2">
                            <small className="text-muted">
                              Recognition Date: {formatDate(project.recognition_date)}
                            </small>
                          </Card.Footer>
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  {/* Summary Card */}
                  <Card className="mt-3 summary-card">
                    <Card.Body className="py-2">
                      <div className="row text-center">
                        <div className="col-6">
                          <strong className="d-block text-sm">Total Projects</strong>
                          <span className="text-primary fs-5">{projectDetails.length}</span>
                        </div>
                        <div className="col-6">
                          <strong className="d-block text-sm">Monthly Total Recognized</strong>
                          <span className="text-success fs-5">
                            {formatAEDStyle(
                              projectDetails.reduce((sum, project) => 
                                sum + parseFloat(project.recognized_amount || 0), 0
                              )
                            )}
                          </span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ) : (
                <div className="text-center p-4 text-muted">
                  <i className="fas fa-inbox fa-2x mb-2"></i>
                  <p>No project details found for this month.</p>
                </div>
              )}
            </Offcanvas.Body>
          </Offcanvas>
        </div>
      </div>
    </div>
  );
};

export default RevenueRecognitionReport;